import type { EnquiryRecord } from '../../types'

export interface ParsedEnquiryDetails {
  isPrivateTour: boolean
  tourTitle: string
  pickupLocation: string
  departureDate: string
  departureTime: string
  adults: number
  children: number
  totalTravelers: number
  estimatedPrice: string
  vehicle: string
  notes: string
  customerName: string
  customerEmail: string
  customerPhone: string
  rawMessage: string
}

export function parseEnquiry(enquiry: EnquiryRecord): ParsedEnquiryDetails {
  const msg = enquiry.message || ''
  const isPrivateTour =
    Boolean(enquiry.tour_title) ||
    Boolean(enquiry.pickup_location) ||
    enquiry.subject?.toLowerCase().includes('private tour') ||
    msg.includes('=== PRIVATE TOUR ENQUIRY ===')

  const extract = (pattern: RegExp): string => {
    const match = msg.match(pattern)
    return match ? match[1].trim() : ''
  }

  const tourTitle =
    enquiry.tour_title ||
    extract(/Tour:\s*([^\n\r]+)/i) ||
    enquiry.subject?.replace(/^Private Tour( Enquiry)?:?\s*/i, '').trim() ||
    'Custom Tour Enquiry'

  const pickupLocation =
    enquiry.pickup_location ||
    extract(/Pickup Location:\s*([^\n\r]+)/i) ||
    ''

  const fromToMatch = msg.match(/Dates:\s*From\s*([^\s]+)\s+to\s+([^\n\r]+)/i)
  const fromDate = (enquiry as any).from_date || (fromToMatch ? fromToMatch[1].trim() : '')
  const toDate = (enquiry as any).to_date || (fromToMatch ? fromToMatch[2].trim() : '')

  const departureMatch = msg.match(/Departure:\s*([^\s]+)\s+at\s+([^\n\r]+)/i)
  const departureDate =
    fromDate && toDate
      ? `${fromDate} → ${toDate}`
      : enquiry.departure_date ||
        (departureMatch ? departureMatch[1].trim() : extract(/Departure Date:\s*([^\n\r]+)/i)) ||
        ''

  const departureTime =
    enquiry.departure_time ||
    (departureMatch ? departureMatch[2].trim() : extract(/Departure Time:\s*([^\n\r]+)/i)) ||
    ''

  const travelersMatch = msg.match(/Travelers:\s*(\d+)/i)
  const explicitTravelers =
    (enquiry as any).travelers_count !== undefined
      ? (enquiry as any).travelers_count
      : travelersMatch
        ? parseInt(travelersMatch[1], 10)
        : undefined

  const adultsMatch = msg.match(/(\d+)\s*Adult/i)
  const childrenMatch = msg.match(/(\d+)\s*Child/i)

  const adults =
    explicitTravelers !== undefined
      ? explicitTravelers
      : enquiry.adults !== undefined
        ? enquiry.adults
        : adultsMatch
          ? parseInt(adultsMatch[1], 10)
          : 1

  const children =
    explicitTravelers !== undefined
      ? 0
      : enquiry.children !== undefined
        ? enquiry.children
        : childrenMatch
          ? parseInt(childrenMatch[1], 10)
          : 0

  const totalTravelers = explicitTravelers !== undefined ? explicitTravelers : adults + children

  let rawPrice =
    enquiry.estimated_price !== undefined
      ? `€${enquiry.estimated_price}`
      : extract(/Estimated Price:\s*([^\n\r]+)/i)
  if (rawPrice && !rawPrice.startsWith('€')) {
    rawPrice = `€${rawPrice}`
  }

  const vehicle =
    enquiry.vehicle ||
    extract(/Selected Vehicle:\s*([^\n\r]+)/i) ||
    extract(/Preferred Vehicle:\s*([^\n\r]+)/i) ||
    'Mercedes Benz Van'

  const notes =
    enquiry.notes ||
    extract(/Special (?:Notes|Requests):\s*([^\n\r]+)/i)

  const customerName =
    enquiry.customer?.name ||
    extract(/Name:\s*([^\n\r]+)/i) ||
    'Customer'

  const customerEmail =
    enquiry.customer?.email ||
    extract(/Email:\s*([^\n\r]+)/i) ||
    ''

  const customerPhone =
    enquiry.customer_phone ||
    enquiry.customer?.phone ||
    extract(/Phone:\s*([^\n\r]+)/i) ||
    ''

  return {
    isPrivateTour,
    tourTitle,
    pickupLocation,
    departureDate,
    departureTime,
    adults,
    children,
    totalTravelers,
    estimatedPrice: rawPrice,
    vehicle,
    notes,
    customerName,
    customerEmail,
    customerPhone,
    rawMessage: msg,
  }
}
